/* Shared PWA install + update helper.
   Captures `beforeinstallprompt` ONCE at module load (the event fires early,
   before most components mount) so any component can trigger the native
   1-click install. Also exposes a 1-click "update to latest version". */

let deferredPrompt = null;
const listeners = new Set();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    listeners.forEach((cb) => cb(true));
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    listeners.forEach((cb) => cb(false));
  });
}

export const canInstall = () => !!deferredPrompt;

/* Subscribe to install-availability changes. Returns unsubscribe fn. */
export const onInstallAvailable = (cb) => {
  listeners.add(cb);
  cb(!!deferredPrompt);
  return () => listeners.delete(cb);
};

/* Trigger the native browser install prompt. Returns "accepted"|"dismissed"|null. */
export const promptInstall = async () => {
  if (!deferredPrompt) return null;
  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  if (outcome === "accepted") deferredPrompt = null;
  return outcome;
};

export const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

/* 1-click update: check for a new service worker, clear the shell cache and
   reload so the freshest deployed version loads immediately. */
export const updateApp = async () => {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    await reg?.update();
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  } catch { /* still reload — network-first SW refetches everything */ }
  window.location.reload();
};
